import React, { useState } from 'react';
import {
  Box, Typography, Button, TextField, Divider,
  IconButton, MenuItem, Select, FormControl, InputLabel,
  CircularProgress, List, ListItemButton, ListItemText, Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useFetchEmailDetailQuery, useUpdateEmailMutation, useReplyToEmailMutation } from '../../store/email/EmailApi';
import { useFetchClubsRegistrationsQuery, useFetchAllContactsQuery } from '../../store/club/ClubApi';
import { useFetchTeamsQuery } from '../../store/team/TeamApi';
import { useFetchStaffQuery } from '../../store/user/UserApi';

const STATUS_OPTIONS = [
  { value: 'open',        label: 'Offen' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'answered',    label: 'Beantwortet' },
  { value: 'done',        label: 'Erledigt' },
  { value: 'junk',        label: 'Junk' },
];

const STATUS_LABEL = {
  open:        'Offen',
  in_progress: 'In Bearbeitung',
  answered:    'Beantwortet',
  done:        'Erledigt',
  outgoing:    'Gesendet',
  booking:     'Anmeldung',
  bounce:      'Unzustellbar',
  junk:        'Junk',
};

const isIncoming = (status) => !['outgoing', 'booking', 'bounce', 'junk'].includes(status);

function EmailBody({ body }) {
  if (!body) return <Typography color="text.secondary">(kein Inhalt)</Typography>;

  if (/<[a-z][\s\S]*>/i.test(body)) {
    return (
      <iframe
        srcDoc={body}
        sandbox="allow-same-origin"
        title="E-Mail Inhalt"
        style={{ width: '100%', border: 'none', minHeight: '300px' }}
        onLoad={e => {
          const doc = e.target.contentDocument;
          if (doc) e.target.style.height = doc.body.scrollHeight + 32 + 'px';
        }}
      />
    );
  }

  return (
    <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {body}
    </Box>
  );
}

export default function EmailDetail({ year, emailId, onBack }) {
  const { data: email, isLoading } = useFetchEmailDetailQuery({ year, emailId });
  const [updateEmail]  = useUpdateEmailMutation();
  const [replyToEmail] = useReplyToEmailMutation();

  const { data: clubRegistrations } = useFetchClubsRegistrationsQuery(year);
  const { data: allContacts }       = useFetchAllContactsQuery(year);
  const { data: staff }             = useFetchStaffQuery(year);
  const { data: teams }             = useFetchTeamsQuery(
    { year, clubLabel: email?.club_label ?? '' },
    { skip: !email?.club_label }
  );

  const [replyText, setReplyText]       = useState('');
  const [replySending, setReplySending] = useState(false);

  if (isLoading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!email)    return null;

  const clubs = clubRegistrations?.map(r => r.club) ?? [];

  const handleUpdate = (patch) => updateEmail({ year, emailId, ...patch });

  const handleClubChange = (e) => {
    const club = clubs.find(c => c.label === e.target.value);
    handleUpdate({ club: club?.id ?? null, team: null });
  };

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setReplySending(true);
    await replyToEmail({ year, emailId, body: replyText });
    setReplyText('');
    setReplySending(false);
  };

  return (
    <Box sx={{ p: 2 }}>

      <Box sx={{ display: { sm: 'none' }, mb: 1 }}>
        <IconButton onClick={onBack}><ArrowBackIcon /></IconButton>
      </Box>

      {/* Kopfzeile */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">{email.subject}</Typography>
        <Typography variant="body2" color="text.secondary">
          Von: {email.sender_email}
          {email.contact_name ? ` (${email.contact_name})` : ''}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {email.received_at
            ? new Date(email.received_at).toLocaleString('de-DE', { dateStyle: 'long', timeStyle: 'short' })
            : ''}
        </Typography>
        {email.assigned_user_name && (
          <Typography variant="body2" color="text.secondary">
            Zuständig: {email.assigned_user_name}
          </Typography>
        )}
      </Box>

      {/* Zuweisungen -- nur fuer eingehende E-Mails */}
      {isIncoming(email.status) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select value={email.status} label="Status"
              onChange={e => handleUpdate({ status: e.target.value })}>
              {STATUS_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Zuständig</InputLabel>
            <Select value={email.assigned_user ?? ''} label="Zuständig"
              onChange={e => handleUpdate({ assigned_user: e.target.value || null })}>
              <MenuItem value=""><em>Niemand</em></MenuItem>
              {(staff ?? []).map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 210 }}>
            <InputLabel>Kontakt</InputLabel>
            <Select value={email.contact ?? ''} label="Kontakt"
              onChange={e => handleUpdate({ contact: e.target.value || null })}>
              <MenuItem value=""><em>Kein Kontakt</em></MenuItem>
              {(allContacts ?? []).map(role => (
                <MenuItem key={role.user.id} value={role.user.id}>
                  {role.user.first_name} {role.user.last_name} ({role.club.name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Verein</InputLabel>
            <Select value={email.club_label ?? ''} label="Verein" onChange={handleClubChange}>
              <MenuItem value=""><em>Kein Verein</em></MenuItem>
              {clubs.map(c => (
                <MenuItem key={c.label} value={c.label}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {email.club_label && (
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Mannschaft</InputLabel>
              <Select value={email.team ?? ''} label="Mannschaft"
                onChange={e => handleUpdate({ team: e.target.value || null })}>
                <MenuItem value=""><em>Keine</em></MenuItem>
                {(teams ?? []).map(t => (
                  <MenuItem key={t.id} value={t.id}>{t.label} {t.counter}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 3, overflowX: 'auto' }}>
        <EmailBody body={email.body} />
      </Box>

      {email.replies?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Verlauf</Typography>
          <List dense disablePadding sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            {email.replies.map(reply => (
              <ListItemButton key={reply.id} divider>
                <ListItemText
                  primary={reply.subject}
                  secondary={`${reply.sender_email} · ${reply.received_at
                    ? new Date(reply.received_at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })
                    : ''}`}
                />
                <Chip label={STATUS_LABEL[reply.status] ?? reply.status} size="small" />
              </ListItemButton>
            ))}
          </List>
        </Box>
      )}

      {isIncoming(email.status) && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Antworten</Typography>
          <TextField
            multiline rows={5} fullWidth
            placeholder="Antwort schreiben..."
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            sx={{ mb: 1 }}
          />
          <Button variant="contained" onClick={handleReply}
            disabled={replySending || !replyText.trim()}>
            {replySending ? <CircularProgress size={20} /> : 'Senden'}
          </Button>
        </Box>
      )}

    </Box>
  );
}
